import { type Component, For } from 'solid-js';

type Props = {
  id: string;
  label: string;
  value: string;
  currencyCode: string;
  hint?: string | undefined;
  disabled?: boolean;
  onChange: (value: string) => void;
};

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫'] as const;

/**
 * The amount pad from the supplied Claude Design action screens. It is a
 * proper button keypad rather than a decorative numeric input so it works
 * consistently on tablets and phones without relying on a platform keyboard.
 */
export const AmountPad: Component<Props> = (props) => {
  const displayValue = () => (props.value === '' ? '0.00' : props.value);
  const currencyMark = () =>
    props.currencyCode === 'GBP'
      ? '£'
      : props.currencyCode === 'USD'
        ? '$'
        : `${props.currencyCode} `;

  const press = (key: (typeof KEYS)[number]) => {
    if (props.disabled) return;
    const current = props.value;
    if (key === '⌫') {
      props.onChange(current.slice(0, -1));
      return;
    }
    if (key === '.') {
      if (!current.includes('.')) props.onChange(current === '' ? '0.' : `${current}.`);
      return;
    }
    const [, fractional = ''] = current.split('.');
    if (current.includes('.') && fractional.length >= 2) return;
    props.onChange(current === '0' ? key : `${current}${key}`);
  };

  return (
    <section class="amount-pad" aria-labelledby={`${props.id}-label`}>
      <span id={`${props.id}-label`} class="action-field-label">
        {props.label}
      </span>
      <output
        class="amount-pad-value"
        aria-live="polite"
        aria-label={`${props.label}: ${displayValue()}`}
      >
        <span aria-hidden="true">{currencyMark()}</span>
        {displayValue()}
      </output>
      <div class="amount-pad-keys" aria-label={`Enter ${props.label.toLowerCase()}`}>
        <For each={KEYS}>
          {(key) => (
            <button
              type="button"
              disabled={props.disabled}
              onClick={() => press(key)}
              aria-label={key === '⌫' ? 'Delete last digit' : key === '.' ? 'Decimal point' : key}
            >
              {key}
            </button>
          )}
        </For>
      </div>
      {props.hint ? <p>{props.hint}</p> : null}
    </section>
  );
};
